import { useMemo, useEffect } from 'react';
import { Box, HStack, Button, Icon, Text, Menu, Portal } from '@chakra-ui/react';
import { NavLink, useParams } from 'react-router-dom';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useContentTypesStore from '@/utils/use-content-type';
import useCollectionTypesStore from '@/utils/use-collection-type';
import { ContentType, CollectionType } from '@/lib/types';
import {
  HomeIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  Bars3Icon,
  RectangleStackIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

interface MenuProps {
  name: string;
  href: string;
  icon?: any;
}

function NavigationItem({ item, isActive }: { item: MenuProps; isActive?: boolean }) {
  return (
    <Button
      variant='ghost'
      display='inline-flex'
      textAlign='center'
      alignItems='center'
      fontSize='sm'
      flexDirection='row'
      px={2}
      height={8}
      color={isActive ? 'brand.700' : 'gray.700'}
    >
      {item.icon && <Icon as={item.icon} mx='auto' h={4} w={4} />}
      <Text fontWeight={isActive ? 'bold' : 'medium'} as='span'>
        {item.name}
      </Text>
    </Button>
  );
}

function DropdownMenu({
  title,
  icon,
  links,
}: {
  title: string;
  icon: React.ForwardRefExoticComponent<any>;
  links: MenuProps[];
}) {
  if (links.length === 0) return null;

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button
          variant='ghost'
          display='inline-flex'
          textAlign='center'
          alignItems='center'
          fontSize='sm'
          flexDirection='row'
          px={2}
          height={8}
        >
          <Icon as={icon} mx='auto' h={4} w={4} />
          <Text fontWeight='medium' as='span'>
            {title}
          </Text>
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content>
            {links.map((link) => (
              <Menu.Item key={link.href} asChild value={link.name}>
                <NavLink to={link.href}>
                  {link.icon && <Icon as={link.icon} h={4} w={4} />}
                  {link.name}
                </NavLink>
              </Menu.Item>
            ))}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

function MobileMenu({
  workspaceId,
  isInAdminGroup,
  contentLinks,
  collectionLinks,
}: {
  workspaceId: string;
  isInAdminGroup: boolean;
  contentLinks: MenuProps[];
  collectionLinks: MenuProps[];
}) {
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant='ghost' size='sm'>
          <Icon as={Bars3Icon} w={4} h={4} />
          Menu
        </Button>
      </Menu.Trigger>
      <Menu.Content>
        <Menu.ItemGroup>
          <Menu.Item asChild value='Home'>
            <NavLink to={`/${workspaceId}/home`}>Home</NavLink>
          </Menu.Item>
          <Menu.Item asChild value='Calendar'>
            <NavLink to={`/${workspaceId}/calendar/my-calendar`}>Calendar</NavLink>
          </Menu.Item>
          {isInAdminGroup && (
            <Menu.Item asChild value='Settings'>
              <NavLink to={`/${workspaceId}/settings`}>Settings</NavLink>
            </Menu.Item>
          )}
        </Menu.ItemGroup>

        {contentLinks.length > 0 && (
          <>
            <Menu.Separator />
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel>Libraries</Menu.ItemGroupLabel>
              {contentLinks.map((item) => (
                <Menu.Item asChild key={item.href} value={item.name}>
                  <NavLink to={item.href}>{item.name}</NavLink>
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </>
        )}

        {collectionLinks.length > 0 && (
          <>
            <Menu.Separator />
            <Menu.ItemGroup>
              <Menu.ItemGroupLabel>Collections</Menu.ItemGroupLabel>
              {collectionLinks.map((item) => (
                <Menu.Item asChild key={item.href} value={item.name}>
                  <NavLink to={item.href}>{item.name}</NavLink>
                </Menu.Item>
              ))}
            </Menu.ItemGroup>
          </>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}

export default function NavigationMenu() {
  const isInAdminGroup = useIsInAdminGroup();
  const { workspaceId = '' } = useParams();
  const { contentTypes, loadContentTypes } = useContentTypesStore();
  const { collectionTypes, loadCollectionTypes } = useCollectionTypesStore();

  useEffect(() => {
    if (workspaceId) {
      loadContentTypes(workspaceId);
      loadCollectionTypes(workspaceId);
    }
  }, [workspaceId, loadContentTypes, loadCollectionTypes]);

  const { contentLinks, collectionLinks } = useMemo(() => {
    const contentItems = contentTypes.map((type: ContentType) => {
      const url = `/${workspaceId}/content-types/${type.id}/contents/workspace-library`;

      return {
        name: type.name,
        href: url,
      };
    });

    const collectionItems = collectionTypes.map((type: CollectionType) => {
      const url = `/${workspaceId}/collection-types/${type.id}/collections/workspace-collections`;

      return {
        name: type.name,
        href: url,
      };
    });

    return { contentLinks: contentItems, collectionLinks: collectionItems };
  }, [contentTypes, collectionTypes, workspaceId]);

  const homeItem = {
    name: 'Home',
    href: `/${workspaceId}/home`,
    icon: HomeIcon,
  };

  const settingsItem = {
    name: 'Settings',
    href: `/${workspaceId}/settings`,
    icon: Cog6ToothIcon,
  };

  const calendarItem = {
    name: 'Calendar',
    href: `/${workspaceId}/calendar/workspace-calendar`,
    icon: CalendarDaysIcon,
  };

  return (
    <HStack gap={0} align={'center'}>
      <Box display={{ base: 'none', lg: 'block' }}>
        <NavLink to={homeItem.href}>
          {({ isActive }) => <NavigationItem item={homeItem} isActive={isActive} />}
        </NavLink>

        <DropdownMenu title='Libraries' icon={Squares2X2Icon} links={contentLinks} />

        <DropdownMenu title='Collections' icon={RectangleStackIcon} links={collectionLinks} />

        <NavLink to={calendarItem.href}>
          {({ isActive }) => <NavigationItem item={calendarItem} isActive={isActive} />}
        </NavLink>

        {isInAdminGroup && (
          <NavLink to={settingsItem.href}>
            {({ isActive }) => <NavigationItem item={settingsItem} isActive={isActive} />}
          </NavLink>
        )}
      </Box>

      <Box display={{ base: 'block', lg: 'none' }}>
        <MobileMenu
          workspaceId={workspaceId}
          isInAdminGroup={isInAdminGroup}
          contentLinks={contentLinks}
          collectionLinks={collectionLinks}
        />
      </Box>
    </HStack>
  );
}
